import React, { useState } from "react";
import dayjs from "dayjs";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import axios from "axios";
import { toast } from "react-toastify";
import {
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
} from "@heroicons/react/24/solid";
import TextField from "../customComponents/customTextField";
import CustomButton from "../customComponents/customButton";
import Loader from "../customComponents/customLoader";
import { upload_file_to_s3 } from "../../utils";
import { serviceUrl, genericHeaders } from "../../utils/apiConfig";

const STATUS_MESSAGES = {
  SUBMITTED: {
    text: "Status Report for this month is submitted. Please check in the Pending tab.",
    style: "bg-blue-50 border border-blue-300 text-blue-700",
    Icon: ClockIcon,
  },
  APPROVED: {
    text: "Status Report for this month is submitted and reviewed. Please check in the History tab.",
    style: "bg-blue-50 border border-blue-300 text-blue-700",
    Icon: CheckCircleIcon,
  },
  REJECTED: {
    text: "Status Report for this month is submitted and reviewed. Please check in the History tab.",
    style: "bg-blue-50 border border-blue-300 text-blue-700",
    Icon: XCircleIcon,
  },
};

const UploadStatusReport = () => {
  const [date, setDate] = useState(null);
  const [fileData, setFileData] = useState({ fileName: "", file: null });
  const [s3Document, setS3Document] = useState(null);
  const [billingHours, setBillingHours] = useState("");
  const [description, setDescription] = useState("");
  const [docLoader, setDocLoader] = useState(false);
  const [loading, setLoading] = useState(false);
  const [statusReportStatus, setStatusReportStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);

  const maxDate = dayjs().endOf("month");

  const checkStatusReportStatus = async (dayjsDate) => {
    const month = dayjsDate.month() + 1;
    const year = dayjsDate.year();
    setStatusReportStatus(null);
    setStatusLoading(true);
    try {
      const response = await axios.get(
        `${serviceUrl}/api/v2/statusReport/my/status?billingPeriodMonth=${month}&billingPeriodYear=${year}`,
        { headers: genericHeaders() }
      );
      if (response.status === 200 && response.data?.data) {
        setStatusReportStatus(response.data.data.status);
      }
    } catch (err) {
      if (err?.response?.status === 404) {
        setStatusReportStatus(null);
      } else {
        toast.warn("Could not verify status report status. Please try again.");
        setStatusReportStatus(null);
      }
    } finally {
      setStatusLoading(false);
    }
  };

  const handleDateChange = (evt) => {
    setDate(evt);
    setFileData({ fileName: "", file: null });
    setS3Document(null);
    setBillingHours("");
    setDescription("");
    checkStatusReportStatus(dayjs(evt));
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const maxSizeInBytes = 10 * 1024 * 1024;
    if (file.size > maxSizeInBytes) {
      toast.error("File size limit is 10MB");
      return;
    }

    setFileData({ fileName: file.name, file });
    setS3Document(null);
    setDocLoader(true);
    try {
      const s3path = await upload_file_to_s3(file);
      const s3Data = s3path?.data?.data;
      if (!s3Data?.fileName) {
        toast.error("File upload failed. Please try again.");
        setFileData({ fileName: "", file: null });
        return;
      }
      setS3Document({
        ...s3Data,
        originalFileName: file.name,
      });
    } catch (err) {
      toast.error("File upload failed. Please try again.");
      setFileData({ fileName: "", file: null });
      console.error(err);
    } finally {
      setDocLoader(false);
    }
  };

  const handleUpload = async () => {
    const selectedDate = dayjs(date);
    const payload = {
      billingHours: Number(billingHours),
      billingPeriodMonth: selectedDate.month() + 1,
      billingPeriodYear: selectedDate.year(),
      description: description,
      statusReport: {
        fileName: s3Document?.fileName,
        folderPath: s3Document?.folderPath,
        timeStamp: s3Document?.timeStamp,
        bucketName: s3Document?.bucketName,
        originalFileName: s3Document?.originalFileName,
        region: s3Document?.region,
      },
    };

    setLoading(true);
    try {
      const response = await axios.post(
        `${serviceUrl}/api/v2/statusReport/upload`,
        payload,
        { headers: genericHeaders() }
      );
      if (response.status === 200 || response.status === 201) {
        toast.success(response.data?.message || "Status report uploaded successfully.");
        setDate(null);
        setFileData({ fileName: "", file: null });
        setS3Document(null);
        setBillingHours("");
        setDescription("");
        setStatusReportStatus(null);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to upload status report. Please try again.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const statusInfo = statusReportStatus ? STATUS_MESSAGES[statusReportStatus] : null;

  return (
    <div className="p-8">
      {loading && <Loader />}
      <div>
        <h2 className="text-center font-semibold text-2xl mb-8">
          Upload Status Report{date ? ` for the month of ${dayjs(date).format("MMMM YYYY")}` : ""}
        </h2>

        <div className="flex flex-col gap-6">
          {/* Month Picker */}
          <div className="flex items-center">
            <span className="text-sm font-medium text-gray-700 mr-5 min-w-[220px]">
              Month <span className="text-red-500 text-xl">*</span>
            </span>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                value={date ? dayjs(date) : null}
                openTo="month"
                views={["month", "year"]}
                onChange={handleDateChange}
                maxDate={maxDate}
                sx={{
                  width: "15rem",
                  bgcolor: "white",
                  "& .MuiInputBase-root": {
                    height: "38px",
                    padding: "0 10px",
                  },
                  borderRadius: "0.375rem",
                }}
              />
            </LocalizationProvider>
            {statusLoading && (
              <div className="ml-4 border-2 border-t-blue-500 border-gray-200 rounded-full w-5 h-5 animate-spin" />
            )}
          </div>

          {/* Status message — shown instead of the rest of the form */}
          {date && statusInfo ? (
            <div className={`flex items-start gap-3 rounded-lg p-4 ${statusInfo.style}`}>
              <statusInfo.Icon className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <p className="text-sm font-medium">{statusInfo.text}</p>
            </div>
          ) : (
            <>
              {/* File Upload */}
              <div className="flex items-center">
                <span className="text-sm font-medium text-gray-700 mr-5 min-w-[220px]">
                  Status Report File <span className="text-red-500 text-xl">*</span>
                </span>
                <div className="relative w-60">
                  <input
                    type="file"
                    id="statusReportFile"
                    onChange={handleFileUpload}
                    disabled={docLoader}
                    className="absolute inset-0 opacity-0 w-full h-full cursor-pointer z-10 disabled:cursor-not-allowed"
                  />
                  <div className="w-60 h-[38px] px-3 border border-gray-300 rounded-md shadow-sm text-sm flex items-center justify-between bg-white">
                    <span className={`truncate ${fileData.fileName ? "text-gray-700" : "text-gray-400"}`}>
                      {fileData.fileName || "Choose a file..."}
                    </span>
                    {docLoader ? (
                      <div className="border-2 border-t-blue-500 border-gray-200 rounded-full w-4 h-4 animate-spin flex-shrink-0" />
                    ) : (
                      <span className="text-blue-500 text-xs flex-shrink-0 ml-1">Browse</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Billing Hours */}
              <TextField
                label="Effective Working Hours"
                value={billingHours}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === "" || (Number(val) >= 0 && Number(val) <= 744)) {
                    setBillingHours(val);
                  }
                }}
                type="number"
                name="billingHours"
                placeHolder="Write in hours"
                isMandatory
              />

              {/* Description */}
              <div className="flex flex-col -mt-4">
                <label className="text-sm font-medium text-gray-700 mb-3">
                  Description <span className="text-red-500 text-xl">*</span>
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Enter description"
                  rows={4}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none"
                />
              </div>

              <div className="flex justify-end mt-2 mb-4">
                <CustomButton
                  label="Submit"
                  onClick={handleUpload}
                  disabled={!date || !s3Document || !billingHours || !description.trim() || docLoader}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default UploadStatusReport;
